import "@/App.css";
import "./styles/tokens.css";
import "./styles/glass.css";
import React, { useEffect, useState, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, useParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { installShortcuts } from "./lib/shortcuts";
import { initSmoothScroll, destroySmoothScroll, attachReveals } from "./lib/motion";
import { isSensitiveRoute } from "./lib/sensitiveRoutes";
import RedirectToPresent from "./components/RedirectToPresent";
import "./lib/auditTokenFetchShim";   // CHG-113 — install once on module import

// Issue #44 — only Landing, Login, and NotFound stay eagerly imported (they're
// on the critical path for every first-time visitor). Every other route is
// React.lazy-loaded below so its module — and any heavy dependency only it
// pulls in (gsap-adjacent chart libs, recharts, tweetnacl, etc.) — ships in
// its own chunk instead of the main bundle. Follows the same pattern already
// proven for the (now retired) Storyboard pages.
import Landing from "./pages/Landing";
// 2026-07-27 CHANGE 2  — Methodology + Calibration-state + Updates pages removed.
// 2026-07-27 CHANGE 8  — Legal + Contact stubs added.
// 2026-07-27 CHANGE 24 — /calibration, /calibration-partners, /calibration-partners/sample-backtest
//                        deleted (same treatment as /methodology). Backend endpoints untouched.
// 2026-07-27 Round 4 · CHANGE 22 — catch-all NotFound page.
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import TopProgressBar from "./components/TopProgressBar";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import RouteTransitionOverlay from "./components/RouteTransitionOverlay";
// Issue #44 — shared Suspense fallback. Was already built (Fix #10b,
// 2026-07-07) for exactly this purpose but never wired into any Suspense
// boundary — confirmed zero importers before this change.
import PageLoadingFallback from "./components/PageLoadingFallback";
import ShortcutsModal from "./components/ShortcutsModal";
import AuthCallback from "./components/AuthCallback";
import RequireAuth from "./components/RequireAuth";
// Issue #43 — the global Counsel floating widget was retired in favour
// of the per-analysis AI Guide (`AiGuideDrawer`) back in Phase I, but
// `<CounselProvider>` stayed mounted here and the widget itself
// (components/analysis/_legacy/Counsel.legacy.jsx) was never actually
// rendered anywhere — confirmed via a DOM query for its own
// `[data-testid='counsel-widget']` matching zero elements app-wide.
// The Cmd/Ctrl+J shortcut and the two remaining "Ask Counsel" buttons
// (CompareView.jsx, TermTooltip.jsx) had no visible effect for the
// same reason. All of it removed together — see _legacy/README.md.
import { TourProvider } from "./components/Tour";
import useKeyboardShortcuts from "./hooks/useKeyboardShortcuts";
import CommandPalette from "./components/CommandPalette";
import { Toaster } from "./components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { WorkspaceProvider } from "./lib/WorkspaceContext";
import { WORKSPACES_ENABLED } from "./lib/featureFlags";
// 2026-07-27 Round 4 · CHANGE 36 — bottom-right build-info strip
// ("UNKNOWN · UNKNOWN · GIT xxx · BUILT xxx") retired from the shell.
// It leaked deploy diagnostics onto every user-facing route and violated
// the marketing-surface-cleanliness rule. Diagnostic access is still
// available via GET /api/deployment-info for anyone who needs the raw
// build metadata; the visual badge is gone.

// Issue #44 — route-split page modules. Grouped in the same order the
// routes appear below purely for readability; the chunk name is what
// actually shows up in the bundle analyzer / network tab.
const HowItWorks = React.lazy(() => import(/* webpackChunkName: "how-it-works" */ "./pages/HowItWorks"));
const LegalTerms = React.lazy(() => import(/* webpackChunkName: "legal-terms" */ "./pages/LegalTerms"));
const LegalPrivacy = React.lazy(() => import(/* webpackChunkName: "legal-privacy" */ "./pages/LegalPrivacy"));
const Contact = React.lazy(() => import(/* webpackChunkName: "contact" */ "./pages/Contact"));
// Phase 17.2 — seven-step decision wizard is now the canonical intake.
// Issue #142 — the legacy single-textbox page that lived at
// /analyses/new/legacy is gone. It created analyses through the
// synchronous POST /analyses, which runs the retired engine, and it was
// reachable only by typing the URL (nothing linked it). The wizard
// superseded it in Phase 17.2; keeping a second intake surface alive
// meant porting its inline out-of-scope flow for a page with no traffic.
const AnalysisNewWizard = React.lazy(() => import(/* webpackChunkName: "analysis-new-wizard" */ "./pages/AnalysisNewWizard"));
const AnalysisPreparing = React.lazy(() => import(/* webpackChunkName: "analysis-preparing" */ "./pages/AnalysisPreparing"));
const AnalysisList = React.lazy(() => import(/* webpackChunkName: "analysis-list" */ "./pages/AnalysisList"));
const AnalysisDetail = React.lazy(() => import(/* webpackChunkName: "analysis-detail" */ "./pages/AnalysisDetail"));
const AnalysisCompare = React.lazy(() => import(/* webpackChunkName: "analysis-compare" */ "./pages/AnalysisCompare"));
const AnalysisReplay = React.lazy(() => import(/* webpackChunkName: "analysis-replay" */ "./pages/AnalysisReplay"));
const CROSS_COMPARE_ENABLED = process.env.REACT_APP_FEATURE_CROSS_COMPARE === "true";
const CompareView = React.lazy(() => import(/* webpackChunkName: "compare-view" */ "./pages/CompareView"));
const WorkspacesList = React.lazy(() => import(/* webpackChunkName: "workspaces-list" */ "./pages/WorkspacesList"));
const WorkspaceSettings = React.lazy(() => import(/* webpackChunkName: "workspace-settings" */ "./pages/WorkspaceSettings"));
const InviteUnavailable = React.lazy(() => import(/* webpackChunkName: "invite-unavailable" */ "./pages/InviteUnavailable"));
const InviteAccept = React.lazy(() => import(/* webpackChunkName: "invite-accept" */ "./pages/InviteAccept"));
const ReviewerPortal = React.lazy(() => import(/* webpackChunkName: "reviewer-portal" */ "./pages/ReviewerPortal"));
const Outcomes = React.lazy(() => import(/* webpackChunkName: "outcomes" */ "./pages/Outcomes"));
const AnalysisReport = React.lazy(() => import(/* webpackChunkName: "analysis-report" */ "./pages/AnalysisReport"));
const VerifyStage = React.lazy(() => import(/* webpackChunkName: "verify-stage" */ "./pages/VerifyStage"));

// Phase J — 9-slide Storyboard presentation, route-exclusive (only ever
// mounted at /analyses/:id/present) and 946 lines — a good code-split
// candidate even though it lives under components/, not pages/.
const Storyboard = React.lazy(() => import(/* webpackChunkName: "storyboard-present" */ "./components/analysis/Storyboard"));
const BranchesView = React.lazy(() => import(/* webpackChunkName: "branches-view" */ "./pages/BranchesView"));
const OutcomePromptForm = React.lazy(() => import(/* webpackChunkName: "outcome-prompt-form" */ "./pages/OutcomePromptForm"));
const ApiKeys = React.lazy(() => import(/* webpackChunkName: "api-keys" */ "./pages/ApiKeys"));
const RegulatorView = React.lazy(() => import(/* webpackChunkName: "regulator-view" */ "./pages/RegulatorView"));
// Issue #44 — AdminPortal (1547 lines, pulls in recharts) is admin-only
// and the single largest module in the eager bundle before this change —
// highest-priority split per the issue's own recommendation.
const AdminPortal = React.lazy(() => import(/* webpackChunkName: "admin-portal" */ "./pages/AdminPortal"));
const AdminTabRedirect = React.lazy(() => import(/* webpackChunkName: "admin-tab-redirect" */ "./pages/admin/AdminTabRedirect"));
const StyleGlass = React.lazy(() => import(/* webpackChunkName: "style-glass" */ "./pages/StyleGlass"));
const Register = React.lazy(() => import(/* webpackChunkName: "register" */ "./pages/Register"));
const Account = React.lazy(() => import(/* webpackChunkName: "account" */ "./pages/Account"));
const EmbedVerify = React.lazy(() => import(/* webpackChunkName: "embed-verify" */ "./pages/EmbedVerify"));
const ShortLinkRedirect = React.lazy(() => import(/* webpackChunkName: "short-link-redirect" */ "./pages/ShortLinkRedirect"));
// Phase O, P, Q — new public/user surfaces
const ImpactClock = React.lazy(() => import(/* webpackChunkName: "impact-clock" */ "./pages/ImpactClock"));
const Ecosystem = React.lazy(() => import(/* webpackChunkName: "ecosystem" */ "./pages/Ecosystem"));
const AdminHitl = React.lazy(() => import(/* webpackChunkName: "admin-hitl" */ "./pages/AdminHitl"));

// Defensive alias — the canonical route is /analyses/:id (plural). Mistyped
// /analysis/:id (singular) URLs used to render a totally blank page (no route
// matched), which produced false-positive "X doesn't render" bug reports.
// This shim preserves any trailing :tab segment and 301-equivalent redirects.
function RedirectAnalysisSingularToPlural() {
    const { id, tab } = useParams();
    const dest = tab ? `/analyses/${id}/${tab}` : `/analyses/${id}`;
    return <Navigate to={dest} replace />;
}

/**
 * AppRouter — detects the OAuth session_id fragment SYNCHRONOUSLY during
 * render (not in useEffect, which fires too late and races the provider's
 * own /me check). If present, route to <AuthCallback> exclusively until
 * the exchange completes and the URL is rewritten.
 */
function AppRouter() {
    const location = useLocation();
    const navigate = useNavigate();
    // Phase 11 — install global 2-key shortcut handlers + navigate hooks.
    useEffect(() => {
        const unsub = installShortcuts();
        const onNewAnalysis = () => navigate("/analyses/new");
        const onHowItWorks = () => navigate("/how-it-works");
        const onCopyPermalink = () => {
            try { navigator.clipboard?.writeText(window.location.href); } catch { /* noop */ }
        };
        window.addEventListener("scope-shortcut:new-analysis", onNewAnalysis);
        window.addEventListener("scope-shortcut:open-how-it-works", onHowItWorks);
        window.addEventListener("scope-shortcut:copy-permalink", onCopyPermalink);
        return () => {
            unsub();
            window.removeEventListener("scope-shortcut:new-analysis", onNewAnalysis);
            window.removeEventListener("scope-shortcut:open-how-it-works", onHowItWorks);
            window.removeEventListener("scope-shortcut:copy-permalink", onCopyPermalink);
        };
    }, [navigate]);
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes location={location}>
            <Route path="/" element={<Landing />} />
            {/* 2026-07-27 CHANGE 2 — /methodology, /methodology/calibration-state,
                /methodology/updates removed. Route matchers dropped so those URLs
                fall through to the 404 handler at the bottom of this switch. */}
            <Route path="/how-it-works" element={<HowItWorks />} />
            {/* 2026-07-27 CHANGE 24 — /calibration route matcher dropped; falls through to 404. */}
            <Route path="/analyses" element={<AnalysisList />} />
            {/* Phase 17.2 — seven-step decision wizard is the canonical
                intake. The /analyses/new/legacy route was removed in #142. */}
            <Route path="/analyses/new" element={<RequireAuth label="Sign in to run analyses"><AnalysisNewWizard /></RequireAuth>} />
            <Route path="/analyses/replay" element={<AnalysisReplay />} />
            {/* Phase M3 — question-variant comparison view. Literal segment
                so this ranks above `/analyses/:id/:tab` in the matcher. */}
            <Route path="/analyses/:id/compare" element={<RequireAuth label="Sign in to compare variants"><AnalysisCompare /></RequireAuth>} />
            {/* Fix #5 (2026-07-07) — async create loader route. Literal
                segment so it ranks above the generic `/analyses/:id/:tab`
                matcher directly below. */}
            <Route path="/analyses/:id/preparing" element={<RequireAuth label="Sign in to view your analysis"><AnalysisPreparing /></RequireAuth>} />
            <Route path="/analyses/:id" element={<AnalysisDetail />} />
            <Route path="/analyses/:id/:tab" element={<AnalysisDetail />} />
            {/* Phase X — consequence-map deep-link with pre-selected domain slug */}
            <Route path="/analyses/:id/consequences/:domainSlug" element={<AnalysisDetail />} />
            <Route path="/analyses/:id/report" element={<AnalysisReport />} />
            {/* #165 — Storyboard (/storyboard, /story, /story-v3) is retired; Present and
                Report cover it. Old URLs redirect to Present, keeping ?token=. */}
            <Route path="/analyses/:id/storyboard" element={<RedirectToPresent />} />
            <Route path="/analyses/:id/story" element={<RedirectToPresent />} />
            <Route path="/analyses/:id/story-v3" element={<RedirectToPresent />} />
            {/* Phase J — 9-slide Storyboard presentation. Read-only,
                works for anon token viewers via the shared analysis endpoint. */}
            <Route path="/analyses/:id/present" element={<Storyboard />} />
            {/* Phase O (19.11) — Impact Clock timeline */}
            <Route path="/impact-clock" element={<RequireAuth><ImpactClock /></RequireAuth>} />
            {/* Phase P (19.12) — public archetype × domain directory */}
            <Route path="/ecosystem" element={<Ecosystem />} />
            {/* Phase Q (19.13) — admin HITL review queue */}
            <Route path="/admin/hitl" element={<RequireAuth><AdminHitl /></RequireAuth>} />
            {/* Phase 17.7 — HITL verify + scenario branches comparison */}
            <Route path="/analyses/:id/verify" element={<RequireAuth label="Sign in to verify and seal this analysis"><VerifyStage /></RequireAuth>} />
            <Route path="/analyses/:id/branches" element={<BranchesView />} />
            {/* Phase 17.9 — calibration outcome capture (auth OR magic-link token) */}
            <Route path="/outcomes/prompt/:promptId" element={<OutcomePromptForm />} />
            {/* Defensive alias: singular /analysis/... → canonical /analyses/... */}
            <Route path="/analysis/:id" element={<RedirectAnalysisSingularToPlural />} />
            <Route path="/analysis/:id/:tab" element={<RedirectAnalysisSingularToPlural />} />
            {/* #166: /api/compare is disabled server-side unless FEATURE_CROSS_COMPARE is on;
                keep the page (and its request) dark in step. Mirror the flag as
                REACT_APP_FEATURE_CROSS_COMPARE when re-enabling. */}
            <Route path="/compare" element={CROSS_COMPARE_ENABLED ? <CompareView /> : <Navigate to="/" replace />} />
            {/* 2026-07-27 CHANGE 24 — /calibration-partners + /calibration-partners/sample-backtest
                deleted. Route matchers dropped so both URLs fall through to the 404 handler. */}
            {/* #170: workspaces are hidden (one personal workspace per user) unless
                REACT_APP_FEATURE_WORKSPACES=true. /invite must never auto-accept. */}
            <Route path="/workspaces" element={WORKSPACES_ENABLED ? <RequireAuth label="Sign in to manage workspaces"><WorkspacesList /></RequireAuth> : <Navigate to="/analyses" replace />} />
            <Route path="/workspaces/:id/settings" element={WORKSPACES_ENABLED ? <RequireAuth label="Sign in to manage workspace settings"><WorkspaceSettings /></RequireAuth> : <Navigate to="/analyses" replace />} />
            <Route path="/workspaces/:id/api-keys" element={<Navigate to="/account/api-keys" replace />} />
            <Route path="/account/api-keys" element={<RequireAuth label="Sign in to manage API keys"><ApiKeys /></RequireAuth>} />
            <Route path="/invite" element={WORKSPACES_ENABLED ? <InviteAccept /> : <InviteUnavailable />} />
            <Route path="/reviewer" element={<RequireAuth label="Sign in to access the reviewer portal"><ReviewerPortal /></RequireAuth>} />
            <Route path="/outcomes" element={<RequireAuth label="Sign in to view outcome tracking"><Outcomes /></RequireAuth>} />
            {/* 2026-07-27 CHANGE 3 — /validation standalone route + ValidationQueue
                page removed. */}
            {/* 2026-07-27 CHANGE 8 — Legal + Contact stub routes. */}
            <Route path="/legal/terms" element={<LegalTerms />} />
            <Route path="/legal/privacy" element={<LegalPrivacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/r/:token" element={<RegulatorView />} />
            <Route path="/admin" element={<RequireAuth label="Sign in to access the admin portal"><AdminPortal /></RequireAuth>} />
            {/* Phase AA — path-based admin routes (e.g. /admin/funnel) redirect to the query-param
                pattern that AdminPortal already consumes. Nested-route folder split is a follow-up. */}
            <Route path="/admin/:tabParam" element={<RequireAuth label="Sign in to access the admin portal"><AdminTabRedirect /></RequireAuth>} />
            {/* Phase 17.1 — glass design system demo (admin-only; gated inside the page) */}
            <Route path="/style/glass" element={<RequireAuth label="Sign in to view the design system"><StyleGlass /></RequireAuth>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/account" element={<RequireAuth label="Sign in to manage your account"><Account /></RequireAuth>} />
            <Route path="/embed/verify/:analysisId" element={<EmbedVerify />} />
            <Route path="/s/:slug" element={<ShortLinkRedirect />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            {/* 2026-07-27 Round 4 · CHANGE 22 — catch-all 404. Any URL that
                doesn't match a route above (including the deleted
                /calibration, /calibration-partners, /methodology,
                /calibration-state, /updates, /validation) falls through
                here. */}
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

function RoutedShell() {
    const location = useLocation();
    const [cheatOpen, setCheatOpen] = useState(false);

    // Phase 19.1a — smooth-scroll (Lenis) + GSAP ScrollTrigger. Idempotent
    // init; skipped entirely when the user prefers reduced motion. Cleanup
    // on unmount prevents leaked RAF handles across hot-reloads.
    useEffect(() => {
        initSmoothScroll();
        return () => destroySmoothScroll();
    }, []);

    // Phase 19.1b-i — after each route change, scan the incoming DOM for
    // `.reveal` nodes and wire fade-up-on-scroll animations. Idempotent
    // (attachReveals marks elements with data-revealed to avoid double-wire).
    // The setTimeout gives the new route one paint to mount before we scan.
    useEffect(() => {
        const t = setTimeout(() => attachReveals(), 50);
        return () => clearTimeout(t);
    }, [location.pathname]);

    // Issue #30 — session recording is disabled by default (see index.html)
    // and opted in per-route here. /analyses/* and /embed/* render private
    // decision content and (for /analyses/*) may carry a live audit-link
    // token in the query string, so they're never recorded.
    // Issue #67 — /invite (workspace invite token) and /outcomes/prompt
    // (anonymous outcome-response magic-link token) carry the same kind
    // of live bearer token in the query string; folded into the shared
    // isSensitiveRoute() predicate instead of a route-specific regex here.
    useEffect(() => {
        const posthog = window.posthog;
        if (!posthog) return;
        if (isSensitiveRoute(location.pathname)) posthog.stopSessionRecording?.();
        else posthog.startSessionRecording?.();
    }, [location.pathname]);

    useKeyboardShortcuts({
        onOpenCheatSheet: () => setCheatOpen(true),
    });
    const reduce = useReducedMotion();

    const variants = reduce
        ? { initial: {}, animate: {}, exit: {} }
        : {
              initial: { opacity: 0 },
              animate: { opacity: 1 },
              exit: { opacity: 0 },
          };

    return (
        <>
            {/* CHG-060 — Skip-to-content link. Visible on Tab from address bar. */}
            <a
                href="#app-main"
                className="skip-to-content"
                data-testid="skip-to-content"
            >
                Skip to content
            </a>
            <TopProgressBar />
            <RouteTransitionOverlay />
            <RouteErrorBoundary>
                {/* Fix (2026-07-08 perf pass) — removed `mode="wait"` and
                    shortened the fade so exit + enter overlap instead of
                    sequencing. Under `mode="wait"` every navigation
                    guaranteed a fully-opacity-0 middle frame (=white
                    background showing through). The overlap variant lets
                    the new route paint over the outgoing one, which reads
                    as an instant transition to the human eye. */}
                <AnimatePresence initial={false}>
                    <motion.div
                        key={location.pathname}
                        data-testid="route-transition"
                        id="app-main"
                        initial={variants.initial}
                        animate={variants.animate}
                        exit={variants.exit}
                        transition={{ duration: 0.12, ease: "easeOut" }}
                    >
                        {/* Issue #44 — shared fallback for every newly-lazy
                            route. Present keeps its own
                            inner Suspense (more specific skeleton), which
                            takes precedence over this one when they're the
                            route rendering. */}
                        <Suspense fallback={<PageLoadingFallback />}>
                            <AppRouter />
                        </Suspense>
                    </motion.div>
                </AnimatePresence>
            </RouteErrorBoundary>
            <ShortcutsModal open={cheatOpen} onClose={() => setCheatOpen(false)} />
            <CommandPalette />
            <Toaster position="bottom-right" richColors />
        </>
    );
}

// Issue #41 — shared server-state cache (dedup + cancellation) for the
// designed-brief / next-steps / consequence-map 202-then-poll resources.
// See docs/adr/0001-tanstack-query-for-server-state.md.
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // These resources are LLM-synthesized and don't change once
            // cached server-side, so there's no value refetching just
            // because the window regained focus or a component remounted.
            refetchOnWindowFocus: false,
            retry: false,
        },
    },
});

function App() {
    return (
        <div className="App">
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <AuthProvider>
                        <WorkspaceProvider>
                            <TourShellGate>
                                <RoutedShell />
                            </TourShellGate>
                        </WorkspaceProvider>
                    </AuthProvider>
                </BrowserRouter>
            </QueryClientProvider>
        </div>
    );
}

/** Inject the TourProvider only when we have the current user — react-joyride
 * keys state to user.created_at to decide auto-prompt behaviour. */
function TourShellGate({ children }) {
    const { user } = useAuth() || {};
    return <TourProvider user={user}>{children}</TourProvider>;
}

export default App;
